import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CharMeter } from '../../components/CharMeter';
import { CopyButton } from '../../components/CopyButton';
import { DraftNotice } from '../../components/DraftNotice';
import { OptionChips } from '../../components/OptionChips';
import { isPlanError } from '../../components/plan/planErrors';
import { UpsellFromError } from '../../components/plan/Upsell';
import { TokenListEditor } from '../../components/TokenListEditor';
import { ApiError } from '../../lib/apiClient';
import { clearDraftsByPrefix } from '../../lib/draftStorage';
import { getErrorMessage } from '../../lib/errors';
import { PLATFORM_OPTIONS } from '../../lib/platforms';
import { useDraft } from '../../lib/useDraft';
import type { PlatformName } from '../../types/api';
import { createBusiness, submitOnboarding } from './accountApi';
import {
  BRIEF_CAPS,
  emptyBrief,
  presetGoalsFor,
  toOnboardingCreate,
  validateBrief,
  type BrandBriefValues,
} from './brandBrief';
import { brandKindCopy } from './brandKind';
import { useAccount } from './useAccount';

/** Which write the submit performs: the first brand, or one more. */
export type BriefFlow = 'onboarding' | 'new-brand';

interface BrandBriefFormProps {
  flow: BriefFlow;
  /** What the form opens with: a parse result, a recovered draft, or empty. */
  initial: BrandBriefValues;
  /** Lines the parser could not place. Shown verbatim so nothing is lost. */
  unmatched?: readonly string[];
  /** Things the parser changed on the way in. */
  warnings?: readonly string[];
  /** When `initial` came out of localStorage, the time it was written. */
  restoredAt?: string | null;
  /** Null until the session's user id is known, which disables autosave. */
  draftKey: string | null;
  /** Throws the answers away and returns to the kind screen. */
  onStartOver: () => void;
}

function toggle<T extends string>(values: readonly T[], value: T): T[] {
  return values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value];
}

/**
 * The one brand form, whichever way the user arrived at it and whichever kind
 * of brand it is.
 *
 * Only the name is required. Everything else is context the pipeline and the
 * newsfeed can use, and the brand fields (tone, tagline, never-say) go
 * straight into the knowledge base on submit so the readiness card does not
 * start at zero. Labels follow the kind; the fields and the request do not,
 * except for the headline, which only a personal brand has.
 *
 * The in-progress values mirror to localStorage as the user types, against an
 * empty pristine reference: a prefilled parse result therefore counts as work
 * worth keeping, and a refresh lands the user back here with it.
 */
export function BrandBriefForm({
  flow,
  initial,
  unmatched = [],
  warnings = [],
  restoredAt = null,
  draftKey,
  onStartOver,
}: BrandBriefFormProps) {
  const navigate = useNavigate();
  const { refresh, setProfile, addBusiness } = useAccount();
  const [values, setValues] = useState<BrandBriefValues>(initial);
  const [noticeAt, setNoticeAt] = useState<string | null>(restoredAt);
  const [showUnmatched, setShowUnmatched] = useState(unmatched.length > 0 || warnings.length > 0);
  const [error, setError] = useState<string | null>(null);
  // A 402 on adding a brand (the plan's brand allowance): drawn as the Upsell.
  const [planError, setPlanError] = useState<ApiError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const copy = brandKindCopy(values.kind);
  const presetGoals = presetGoalsFor(values.kind);

  useDraft(draftKey, values, emptyBrief(values.kind));

  function patch(changes: Partial<BrandBriefValues>) {
    setError(null);
    setValues((current) => ({ ...current, ...changes }));
  }

  function startOver() {
    if (draftKey !== null) clearDraftsByPrefix(draftKey);
    onStartOver();
  }

  async function handleSubmit() {
    const message = validateBrief(values);
    if (message) {
      setError(message);
      return;
    }

    setError(null);
    setPlanError(null);
    setIsSubmitting(true);
    try {
      const body = toOnboardingCreate(values);
      if (flow === 'onboarding') {
        const result = await submitOnboarding(body);
        // Update the cached profile before navigating so the onboarding gate
        // sees the completed flag instead of bouncing back here; the refresh
        // then brings the new brand into the list.
        setProfile(result.profile);
        refresh();
      } else {
        // The list gains the brand and it becomes active before the dashboard
        // renders, so nothing flashes the previous brand's data.
        addBusiness(await createBusiness(body));
      }
      // The answers are on file now, so the local copy is dead weight. Sweeping
      // by prefix also takes any key an earlier build of this form left.
      if (draftKey !== null) clearDraftsByPrefix(draftKey);
      void navigate('/', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'ONBOARDING_ALREADY_COMPLETED') {
        // Setup was filed elsewhere; the draft can only be stale.
        if (draftKey !== null) clearDraftsByPrefix(draftKey);
        refresh();
        void navigate('/', { replace: true });
        return;
      }
      if (isPlanError(caught)) {
        setPlanError(caught);
        return;
      }
      if (caught instanceof ApiError && caught.code === 'BUSINESS_LIMIT_REACHED') {
        setError('This account already holds the maximum number of brands.');
        return;
      }
      setError(getErrorMessage(caught, 'Setup did not save. Try again.'));
    } finally {
      setIsSubmitting(false);
    }
  }

  const customGoals = values.goals.filter((goal) => !presetGoals.includes(goal));
  const submitLabel =
    flow === 'onboarding'
      ? isSubmitting
        ? 'Saving setup'
        : 'Finish setup'
      : isSubmitting
        ? 'Adding brand'
        : 'Add brand';

  return (
    <section className="wizard">
      {noticeAt ? (
        <DraftNotice
          savedAt={noticeAt}
          discardLabel="Start over"
          onDiscard={startOver}
          onDismiss={() => setNoticeAt(null)}
        />
      ) : null}

      {showUnmatched ? (
        <aside className="brief-unmatched" aria-label="Lines we could not place">
          <div className="brief-unmatched__head">
            <p className="brief-unmatched__title">
              {unmatched.length > 0
                ? 'We could not place these lines. Copy each one into the field you meant.'
                : 'Notes on what we read.'}
            </p>
            <button
              className="draft-notice__close"
              type="button"
              aria-label="Dismiss these notes"
              onClick={() => setShowUnmatched(false)}
            >
              ×
            </button>
          </div>
          {unmatched.length > 0 ? (
            <ul className="brief-unmatched__list">
              {unmatched.map((line, index) => (
                <li className="brief-unmatched__item" key={`${index}:${line}`}>
                  <span className="brief-unmatched__line">{line}</span>
                  <CopyButton text={line} label="Copy" />
                </li>
              ))}
            </ul>
          ) : null}
          {warnings.length > 0 ? (
            <ul className="brief-unmatched__warnings">
              {warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          ) : null}
        </aside>
      ) : null}

      <form
        className="brief-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <p className="brief-form__kind">
          <span className="badge badge--neutral">{copy.tag}</span>
          <button className="link-button" type="button" onClick={startOver}>
            Change
          </button>
        </p>

        <div className="brief-form__row">
          <label className="field">
            <span className="field__label">
              {copy.name.label} <span className="brief-form__required">required</span>
            </span>
            <input
              className="field__input"
              type="text"
              maxLength={BRIEF_CAPS.business_name}
              placeholder={copy.name.placeholder}
              autoComplete={values.kind === 'personal' ? 'name' : 'organization'}
              value={values.business_name}
              onChange={(event) => patch({ business_name: event.target.value })}
            />
            <span className="field__hint">{copy.name.hint}</span>
          </label>

          {copy.headline ? (
            <label className="field">
              <span className="field__label">{copy.headline.label}</span>
              <input
                className="field__input"
                type="text"
                maxLength={BRIEF_CAPS.headline}
                placeholder={copy.headline.placeholder}
                autoComplete="organization-title"
                value={values.headline}
                onChange={(event) => patch({ headline: event.target.value })}
              />
              <span className="field__hint">{copy.headline.hint}</span>
            </label>
          ) : (
            <label className="field">
              <span className="field__label">{copy.industry.label}</span>
              <input
                className="field__input"
                type="text"
                maxLength={BRIEF_CAPS.industry}
                placeholder={copy.industry.placeholder}
                value={values.industry}
                onChange={(event) => patch({ industry: event.target.value })}
              />
              <span className="field__hint">{copy.industry.hint}</span>
            </label>
          )}
        </div>

        {copy.headline ? (
          <label className="field">
            <span className="field__label">{copy.industry.label}</span>
            <input
              className="field__input"
              type="text"
              maxLength={BRIEF_CAPS.industry}
              placeholder={copy.industry.placeholder}
              value={values.industry}
              onChange={(event) => patch({ industry: event.target.value })}
            />
            <span className="field__hint">{copy.industry.hint}</span>
          </label>
        ) : null}

        <label className="field">
          <span className="field__label">{copy.valueProposition.label}</span>
          <textarea
            className="field__input"
            rows={3}
            maxLength={BRIEF_CAPS.value_proposition}
            placeholder={copy.valueProposition.placeholder}
            value={values.value_proposition}
            onChange={(event) => patch({ value_proposition: event.target.value })}
          />
          <span className="field__hint">
            {copy.valueProposition.hint} {values.value_proposition.length} of{' '}
            {BRIEF_CAPS.value_proposition}
          </span>
        </label>

        <label className="field">
          <span className="field__label">{copy.targetAudience.label}</span>
          <textarea
            className="field__input"
            rows={3}
            maxLength={BRIEF_CAPS.target_audience}
            placeholder={copy.targetAudience.placeholder}
            value={values.target_audience}
            onChange={(event) => patch({ target_audience: event.target.value })}
          />
          <span className="field__hint">
            {copy.targetAudience.hint} {values.target_audience.length} of{' '}
            {BRIEF_CAPS.target_audience}
          </span>
        </label>

        <div className="brief-form__row">
          <label className="field">
            <span className="field__label">{copy.tone.label}</span>
            <input
              className="field__input"
              type="text"
              maxLength={BRIEF_CAPS.tone}
              placeholder={copy.tone.placeholder}
              value={values.tone}
              onChange={(event) => patch({ tone: event.target.value })}
            />
            <span className="field__hint">{copy.tone.hint}</span>
          </label>

          <label className="field">
            <span className="field__label">{copy.tagline.label}</span>
            <input
              className="field__input"
              type="text"
              maxLength={BRIEF_CAPS.tagline}
              placeholder={copy.tagline.placeholder}
              value={values.tagline}
              onChange={(event) => patch({ tagline: event.target.value })}
            />
            <CharMeter text={values.tagline} />
          </label>
        </div>

        <TokenListEditor
          label="Words to avoid"
          hint={copy.neverSayHint}
          placeholder={values.kind === 'personal' ? 'guru' : 'artisanal'}
          addLabel="Add"
          tokens={values.never_say}
          onChange={(never_say) => patch({ never_say })}
        />

        <OptionChips
          legend="Platforms"
          hint="Where the posts go. Each post picks one of these."
          options={PLATFORM_OPTIONS}
          selected={values.platforms}
          onToggle={(value) =>
            patch({ platforms: toggle(values.platforms, value as PlatformName) })
          }
        />

        <div className="brief-form__goals">
          <OptionChips
            legend="Goals"
            hint="What the content should do for you."
            options={copy.goals}
            selected={values.goals}
            onToggle={(value) => patch({ goals: toggle(values.goals, value) })}
          />
          <TokenListEditor
            label="Another goal"
            hint="Anything the list above misses."
            placeholder={copy.customGoalPlaceholder}
            addLabel="Add goal"
            tokens={customGoals}
            onChange={(tokens) =>
              patch({
                goals: [...values.goals.filter((goal) => presetGoals.includes(goal)), ...tokens],
              })
            }
          />
        </div>

        {error ? (
          <p className="form__error" role="alert">
            {error}
          </p>
        ) : null}
        <UpsellFromError error={planError} />

        <div className="wizard__actions">
          <button
            className="button button--ghost"
            type="button"
            onClick={startOver}
            disabled={isSubmitting}
          >
            Start over
          </button>
          <button className="button button--primary" type="submit" disabled={isSubmitting}>
            {submitLabel}
          </button>
        </div>
      </form>
    </section>
  );
}
